import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WalterLoader from '../../shared/WalterLoader';
import {
  DEFAULT_SETTINGS, RIV_LIB, VMIN, TYPE_COLORS, TYPE_LABELS,
  autoTexts, download, drawMap, fmtVol, pngDataUrl, rankSources, svgString,
} from './waterOriginMap';
import '../../../styles/WaterOrigin.css';

const API = process.env.REACT_APP_API_URL;
const TEXT_FIELDS = [["intro", "Texte d'introduction"], ["surf", "Eaux superficielles"], ["sout", "Eaux souterraines"]];

async function call(path, options) {
  const response = await fetch(`${API}${path}`, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.error) throw new Error(body.error || `Erreur ${response.status}`);
  return body;
}
const post = (path, body) => call(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

const formatDate = iso => iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';

// Outil « Origine de l'eau potable » (menu Outils, /outils/origine-eau?building=…) : choix du
// bâtiment parmi ceux visibles par le compte (/home_overview), puis sa page.
export default function WaterOriginTool() {
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingId = searchParams.get('building') || '';
  const [buildings, setBuildings] = useState(null);
  const [listError, setListError] = useState(null);

  useEffect(() => {
    call('/home_overview')
      .then(data => setBuildings((data.buildings || []).slice().sort((a, b) =>
        `${a.client_name || ''} ${a.name || ''}`.localeCompare(`${b.client_name || ''} ${b.name || ''}`, 'fr'))))
      .catch(e => setListError(e.message));
  }, []);

  const building = buildings?.find(b => b._id === buildingId) || null;
  const picker = (
    <select className="wo-picker" value={buildingId} disabled={!buildings}
      onChange={e => setSearchParams(e.target.value ? { building: e.target.value } : {})}>
      <option value="">{buildings ? 'Choisir un bâtiment…' : 'Chargement des bâtiments…'}</option>
      {(buildings || []).map(b => (
        <option key={b._id} value={b._id}>{b.client_name ? `${b.client_name} · ` : ''}{b.name}{b.city ? ` (${b.city})` : ''}</option>
      ))}
    </select>
  );

  if (!buildingId) return (
    <div className="wo-page">
      <div className="wo-header">
        <div>
          <h1>Origine probable de l'eau potable</h1>
          <p className="wo-sub">Captages d'eau potable déclarés à la BNPE autour du bâtiment : carte et textes pour le rapport.</p>
        </div>
        <div className="wo-header-actions">{picker}</div>
      </div>
      {listError && <div className="wo-err">{listError}</div>}
    </div>
  );
  return <WaterOriginPage key={buildingId} building_id={buildingId} client_id={building?.client_id} picker={picker} />;
}

// Origine probable de l'eau potable du bâtiment (captages BNPE, cf. api/water_origin.py) : carte
// à régler puis valider (PNG envoyé sur Cloudinary, repris par walter-rapport), textes de l'encart.
function WaterOriginPage({ building_id, client_id, picker }) {
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const [origin, setOrigin] = useState(null);
  const [layers, setLayers] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [manual, setManual] = useState({});
  const [busy, setBusy] = useState("Chargement…");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [fontsTick, setFontsTick] = useState(0);

  const applyOrigin = useCallback(data => {
    setOrigin(data);
    setSettings({ ...DEFAULT_SETTINGS, ...(data.settings || {}) });
    setManual(data.manual || {});
    setDirty(false);
  }, []);

  const compute = useCallback(async () => {
    setBusy("Recherche des captages autour du site (prélèvements BNPE, référentiel Hub'Eau)… quelques secondes.");
    setError(null);
    try { applyOrigin((await post(`/compute_water_origin/${building_id}`)).origin); }
    catch (e) { setError(e.message); }
    finally { setBusy(null); }
  }, [building_id, applyOrigin]);

  useEffect(() => {
    call(`/get_water_origin/${building_id}`)
      .then(({ origin: data }) => { if (data) { applyOrigin(data); setBusy(null); } else compute(); })
      .catch(e => { setError(e.message); setBusy(null); });
  }, [building_id, applyOrigin, compute]);

  const dept = origin?.site?.dep;
  useEffect(() => {
    if (!dept || layers?._id === dept) return;
    setBusy(b => b || "Préparation du fond de carte du département… jusqu'à 30 secondes la première fois.");
    call(`/get_map_layers/${dept}`)
      .then(setLayers)
      .catch(e => setError(e.message))
      .finally(() => setBusy(null));
  }, [dept, layers]);

  useEffect(() => { document.fonts?.ready.then(() => setFontsTick(t => t + 1)); }, []);

  const analysis = useMemo(() => origin ? rankSources(origin, settings.rayon) : null, [origin, settings.rayon]);
  const auto = useMemo(() => analysis ? autoTexts(analysis) : null, [analysis]);
  const texts = useMemo(() => auto && Object.fromEntries(Object.keys(auto).map(k => [k, manual[k] || auto[k]])), [auto, manual]);

  useEffect(() => {
    if (origin && layers && svgRef.current) drawMap(svgRef.current, { origin, layers, settings, analysis, texts });
  }, [origin, layers, settings, analysis, texts, fontsTick]);

  const set = (key, value) => { setSettings(s => ({ ...s, [key]: value })); setDirty(true); };
  const setText = (key, value) => { setManual(m => ({ ...m, [key]: value })); setDirty(true); };
  const fileName = ext => `origine_eau_${(origin?.site?.nom || 'site').normalize('NFD').replace(/\p{Mn}/gu, '').replace(/[^A-Za-z0-9]+/g, '_')}.${ext}`;

  const save = async withMap => {
    setBusy(withMap ? "Enregistrement de la carte…" : "Enregistrement…");
    setError(null);
    try {
      const body = { settings, manual };
      if (withMap) body.map_png = await pngDataUrl(svgRef.current);
      applyOrigin((await post(`/save_water_origin/${building_id}`, body)).origin);
      setNotice(withMap ? "Carte validée : l'image est enregistrée et reprise par le rapport." : "Réglages enregistrés.");
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const exportSvg = async () => {
    const url = URL.createObjectURL(new Blob([await svgString(svgRef.current)], { type: 'image/svg+xml' }));
    download(url, fileName('svg'));
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const exportPng = async () => download(await pngDataUrl(svgRef.current), fileName('png'));

  const warnings = [];
  if (analysis) {
    const approx = analysis.list.filter(o => o.approx).length;
    if (approx) warnings.push(`${approx} captage(s) du rayon sont placés au centre de leur commune dans la BNPE : leur position et leur distance sont approchées.`);
    const unknown = analysis.list.filter(o => o.type === 'inconnu').length;
    if (unknown) warnings.push(`${unknown} captage(s) du rayon ont un milieu indéterminé (compteurs de réservoir, de réseau…) : à vérifier avant de conclure.`);
  }
  (origin?.warnings || []).forEach(w => warnings.push(w));
  const mapOutdated = origin?.map_url && (origin.map_saved_at || '') < (origin.computed_at || '');
  const best = analysis?.list[0]?.score || 1;

  return (
    <div className="wo-page">
      <div className="wo-header">
        <div>
          <h1>Origine probable de l'eau potable</h1>
          {origin && <p className="wo-sub">
            {origin.site.nom} ({origin.site.insee}) · position : {origin.site.position === 'adresse' ? "adresse du bâtiment" : "centre de la commune (bâtiment non géocodé)"}
            {' '}· {origin.ouvrages.length} captages AEP à moins de {origin.rayon_max} km · volumes {origin.annees?.join(' et ')} · calcul du {formatDate(origin.computed_at)}
          </p>}
        </div>
        <div className="wo-header-actions">
          {picker}
          {client_id && <button className="wo-btn ghost" onClick={() => navigate(`/client/${client_id}/${building_id}`)}>Dashboard du bâtiment</button>}
          <button className="wo-btn ghost" onClick={compute} disabled={!!busy}>Recalculer</button>
        </div>
      </div>

      {busy && <div className="wo-busy"><WalterLoader size={32} /> {busy}</div>}
      {error && <div className="wo-err">{error}</div>}
      {notice && !busy && <div className="wo-ok">{notice}</div>}

      {origin && (
        <div className="wo-grid">
          <aside className="wo-aside">
            <div className="wo-card">
              <h2>Recherche</h2>
              <label><span>Rayon de recherche : <b>{settings.rayon} km</b></span>
                <input type="range" min="3" max={origin.rayon_max} step="1" value={settings.rayon} onChange={e => set('rayon', +e.target.value)} />
              </label>
              {origin.udi?.length > 0 && (
                <div className="wo-small">Réseau de distribution de la commune (ARS) : {origin.udi.map(u => u.nom + (u.quartier ? ` (${u.quartier})` : '')).join(', ')}.
                  {' '}Le service qui l'exploite peut confirmer l'origine.</div>
              )}
            </div>

            <div className="wo-card">
              <h2>Encart</h2>
              {TEXT_FIELDS.map(([key, title]) => (
                <label key={key}>{title}
                  <textarea rows={key === 'intro' ? 3 : 1} value={manual[key] ?? auto?.[key] ?? ''} onChange={e => setText(key, e.target.value)} />
                </label>
              ))}
              <button className="wo-btn ghost" onClick={() => { setManual({}); setDirty(true); }}>Revenir aux propositions automatiques</button>
              <label className="wo-check"><input type="checkbox" checked={settings.box} onChange={e => set('box', e.target.checked)} /> Afficher l'encart sur la carte</label>
              {settings.box && (
                <label>Position de l'encart
                  <select value={settings.pos} onChange={e => set('pos', e.target.value)}>
                    <option value="bl">En bas à gauche</option><option value="br">En bas à droite</option>
                    <option value="tr">En haut à droite</option><option value="tl">En haut à gauche</option>
                  </select>
                </label>
              )}
            </div>

            <div className="wo-card">
              <h2>Affichage</h2>
              <label><span>Cours d'eau : <b>{RIV_LIB[settings.riv]}</b></span>
                <input type="range" min="0" max={RIV_LIB.length - 1} step="1" value={settings.riv} onChange={e => set('riv', +e.target.value)} />
              </label>
              <label><span>Points de captage : <b>{settings.vmin ? `masqués sous ${fmtVol(VMIN[settings.vmin])}/an` : 'tous'}</b></span>
                <input type="range" min="0" max={VMIN.length - 1} step="1" value={settings.vmin} onChange={e => set('vmin', +e.target.value)} />
              </label>
              <label className="wo-check"><input type="checkbox" checked={settings.vminOut} onChange={e => set('vminOut', e.target.checked)} /> Seulement hors du cercle</label>
              <label className="wo-check"><input type="checkbox" checked={settings.labels} onChange={e => set('labels', e.target.checked)} /> Étiqueter les principaux ouvrages</label>
              <label className="wo-check"><input type="checkbox" checked={settings.legend} onChange={e => set('legend', e.target.checked)} /> Afficher la légende</label>
              <div className="wo-small">Masque l'affichage seulement : le classement ne change pas.</div>
            </div>

            <div className="wo-card">
              <h2>Valider et exporter</h2>
              <button className="wo-btn" onClick={() => save(true)} disabled={!!busy || !layers}>Valider la carte pour le rapport</button>
              <button className="wo-btn ghost" onClick={() => save(false)} disabled={!!busy || !dirty}>Enregistrer les réglages</button>
              <div className="wo-row">
                <button className="wo-btn ghost" onClick={exportPng} disabled={!layers}>PNG</button>
                <button className="wo-btn ghost" onClick={exportSvg} disabled={!layers}>SVG</button>
              </div>
              <div className="wo-small">
                {origin.map_url
                  ? <>Carte validée le {formatDate(origin.validated_at)}{origin.validated_by ? ` par ${origin.validated_by}` : ''} · <a href={origin.map_url} target="_blank" rel="noreferrer">voir l'image</a>
                    {mapOutdated && <b> · recalculée depuis : à valider de nouveau</b>}</>
                  : "Pas encore de carte validée : le rapport n'aura que le texte de l'encart."}
                {dirty && <b> · Modifications non enregistrées.</b>}
              </div>
            </div>
          </aside>

          <main className="wo-main">
            <div className="wo-map">{layers ? <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Carte des captages BNPE" /> : <div className="wo-map-placeholder" />}</div>
            <div className="wo-card">
              <h2>Captages AEP dans le rayon, classés par probabilité</h2>
              {warnings.map(w => <div className="wo-warn" key={w}>{w}</div>)}
              <div className="wo-table-wrap"><table className="wo-table">
                <thead><tr><th>Ouvrage</th><th>Commune</th><th>Milieu</th><th className="n">Distance</th><th className="n">Volume moyen</th><th className="n">Score</th></tr></thead>
                <tbody>
                  {analysis.list.slice(0, 25).map(o => (
                    <tr key={o.code} className={o.code === analysis.surf?.code || o.code === analysis.sout?.code ? 'pick' : ''}>
                      <td>{o.nom}<div className="wo-small">{o.codes.join(', ')}{o.riviere ? ` · ${o.riviere}` : ''}{o.nappe ? ` · nappe ${o.nappe}` : ''}</div></td>
                      <td>{o.commune}{o.dep !== dept && <span className="wo-small"> ({o.dep})</span>}{o.inside ? ' ●' : ''}</td>
                      <td><span className="wo-dot" style={{ background: TYPE_COLORS[o.type] }} />{TYPE_LABELS[o.type]}<div className="wo-small">{o.type_src}</div></td>
                      <td className="n">{o.approx ? '≈ ' : ''}{o.d.toFixed(1)} km</td>
                      <td className="n">{fmtVol(o.vol)}</td>
                      <td className="n">{Math.round(100 * o.score / best)}</td>
                    </tr>
                  ))}
                  {!analysis.list.length && <tr><td colSpan="6">Aucun captage AEP à moins de {settings.rayon} km. Élargissez le rayon.</td></tr>}
                </tbody>
              </table></div>
              <div className="wo-small">Score = volume moyen annuel ÷ (distance + 1 km)², ramené à 100 pour la ressource la plus probable ;
                une ressource dans la commune du site compte à 1 km au plus. ● = dans la commune du site, ≈ = coordonnées approchées.
                Le résultat reste une hypothèse : le syndicat ou la régie d'eau confirme l'origine réelle.</div>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}
